import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Paper,
  Text,
  Button,
  Group,
  Stack,
  Loader,
  Alert,
  TextInput,
  Avatar,
  Box,
  Center,
  Modal,
} from "@mantine/core";
import {
  IconFilter,
  IconTrophy,
  IconSearch,
  IconX,
  IconArrowUp,
  IconArrowDown,
  IconFlame,
  IconUsers,
} from "@tabler/icons-react";

function LeaderboardTab() {
  const { getVerifiedJWT, user } = useAuth();

  const [trainingData, setTrainingData] = useState(null);
  const [recruitingData, setRecruitingData] = useState(null);
  const [streakData, setStreakData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [isRefreshingLeaderboard, setIsRefreshingLeaderboard] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedMetric, setSelectedMetric] = useState("total_minutes");
  const [showMetricModal, setShowMetricModal] = useState(false);
  const [showTimePeriodModal, setShowTimePeriodModal] = useState(false);
  const [showOrgLevelModal, setShowOrgLevelModal] = useState(false);
  const [selectedOrgLevelFilter, setSelectedOrgLevelFilter] = useState(null);
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangePreset, setDateRangePreset] = useState("all_time");

  const isManager =
    user?.role === "MANAGER" ||
    user?.role === "COMPANY_ADMIN" ||
    user?.isManager;

  const availableMetrics = [
    {
      value: "total_minutes",
      label: "Total Minutes",
      section: "Training",
    },
    {
      value: "completed_videos",
      label: "Completed Videos",
      section: "Training",
    },
    ...(isManager
      ? [
          {
            value: "total_score",
            label: "Total Score",
            section: "Recruiting",
          },
          { value: "leads", label: "Leads", section: "Recruiting" },
          {
            value: "first_meetings",
            label: "1st Meetings",
            section: "Recruiting",
          },
          {
            value: "additional_meetings",
            label: "Additional Meetings",
            section: "Recruiting",
          },
          {
            value: "offers",
            label: "Offers Sent",
            section: "Recruiting",
          },
          { value: "signed", label: "Signed", section: "Recruiting" },
        ]
      : []),
  ];

  const datePresets = [
    { value: "all_time", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "this_week", label: "This Week" },
    { value: "last_7_days", label: "Last 7 Days" },
    { value: "this_month", label: "This Month" },
    { value: "last_30_days", label: "Last 30 Days" },
    { value: "this_quarter", label: "This Quarter" },
    { value: "last_quarter", label: "Last Quarter" },
    { value: "this_year", label: "This Year" },
    { value: "last_year", label: "Last Year" },
    { value: "custom", label: "Custom Range" },
  ];

  const isRecruitingMetric = metric => {
    return [
      "total_score",
      "leads",
      "first_meetings",
      "additional_meetings",
      "offers",
      "signed",
    ].includes(metric);
  };

  const fetchTrainingLeaderboard = async (
    filterStartDate = null,
    filterEndDate = null,
    orgLevelFilter = null
  ) => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Failed to authenticate");

      const body = {};
      if (filterStartDate) body.start_date = filterStartDate;
      if (filterEndDate) body.end_date = filterEndDate;
      if (orgLevelFilter) {
        body.filter_value = orgLevelFilter.filter_value;
        body.filter_type = "org_level";
      }

      const response = await fetch(getApiEndpoint("/v1/training/leaderboard"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setTrainingData(result.data);
        }
      } else {
        throw new Error("Failed to fetch training leaderboard");
      }
    } catch (err) {
      throw err;
    }
  };

  const fetchRecruitingLeaderboard = async (
    filterStartDate = null,
    filterEndDate = null,
    orgLevelFilter = null
  ) => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Failed to authenticate");

      const body = {};
      if (filterStartDate) body.start_date = filterStartDate;
      if (filterEndDate) body.end_date = filterEndDate;
      if (orgLevelFilter) {
        body.filter_value = orgLevelFilter.filter_value;
        body.filter_type = "org_level";
      }

      const response = await fetch(
        getApiEndpoint("/v1/recruiting/leaderboard"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setRecruitingData(result.data);
        }
      } else {
        throw new Error("Failed to fetch recruiting leaderboard");
      }
    } catch (err) {
      throw err;
    }
  };

  const fetchStreakData = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/training/streaks"), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setStreakData(result.data);
        }
      }
    } catch (err) {
      // Silent error - streak is optional
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchTrainingLeaderboard(startDate, endDate, selectedOrgLevelFilter),
        isManager
          ? fetchRecruitingLeaderboard(
              startDate,
              endDate,
              selectedOrgLevelFilter
            )
          : Promise.resolve(),
        fetchStreakData(),
      ]);
    } catch (err) {
      setError(err.message || "Failed to load leaderboard data");
    } finally {
      setLoading(false);
    }
  };

  const refreshLeaderboardData = async (
    filterStartDate = null,
    filterEndDate = null,
    orgLevelFilter = selectedOrgLevelFilter
  ) => {
    setIsRefreshingLeaderboard(true);

    try {
      await Promise.all([
        fetchTrainingLeaderboard(
          filterStartDate,
          filterEndDate,
          orgLevelFilter
        ),
        isManager && isRecruitingMetric(selectedMetric)
          ? fetchRecruitingLeaderboard(
              filterStartDate,
              filterEndDate,
              orgLevelFilter
            )
          : Promise.resolve(),
      ]);
    } catch (err) {
      setError(err.message || "Failed to refresh leaderboard data");
    } finally {
      setIsRefreshingLeaderboard(false);
    }
  };

  const handleOrgLevelFilterChange = filter => {
    setSelectedOrgLevelFilter(filter);
    setShowOrgLevelModal(false);
    refreshLeaderboardData(startDate, endDate, filter);
  };

  const applyDateRangePreset = preset => {
    setDateRangePreset(preset);

    if (preset === "custom") return;

    const { start, end } = calculateDateRange(preset);
    const startDateStr = start ? formatDateForInput(start) : "";
    const endDateStr = end ? formatDateForInput(end) : "";

    setStartDate(startDateStr);
    setEndDate(endDateStr);

    refreshLeaderboardData(startDateStr, endDateStr);
  };

  const calculateDateRange = preset => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start, end;

    switch (preset) {
      case "today":
        start = today;
        end = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          23,
          59,
          59
        );
        break;
      case "this_week":
        const dayOfWeek = today.getDay();
        start = new Date(today);
        start.setDate(today.getDate() - dayOfWeek);
        end = now;
        break;
      case "last_7_days":
        start = new Date(today);
        start.setDate(today.getDate() - 7);
        end = now;
        break;
      case "this_month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = now;
        break;
      case "last_30_days":
        start = new Date(today);
        start.setDate(today.getDate() - 30);
        end = now;
        break;
      case "this_quarter":
        const currentQuarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), currentQuarter * 3, 1);
        end = now;
        break;
      case "last_quarter":
        const lastQuarterNum = Math.floor(today.getMonth() / 3) - 1;
        if (lastQuarterNum < 0) {
          start = new Date(today.getFullYear() - 1, 9, 1);
          end = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59);
        } else {
          start = new Date(today.getFullYear(), lastQuarterNum * 3, 1);
          const lastQuarterEndMonth = lastQuarterNum * 3 + 2;
          end = new Date(
            today.getFullYear(),
            lastQuarterEndMonth + 1,
            0,
            23,
            59,
            59
          );
        }
        break;
      case "this_year":
        start = new Date(today.getFullYear(), 0, 1);
        end = now;
        break;
      case "last_year":
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59);
        break;
      case "all_time":
      default:
        return { start: null, end: null };
    }

    return { start, end };
  };

  const formatDateForInput = date => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getTimePeriodLabel = () => {
    if (dateRangePreset === "custom" && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const formatter = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
      });
      return `${formatter.format(start)} – ${formatter.format(end)}`;
    }
    return (
      datePresets.find(p => p.value === dateRangePreset)?.label || "All Time"
    );
  };

  const formatDatePillLabel = dateValue => {
    if (!dateValue) return null;
    const date = new Date(`${dateValue}T00:00:00`);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const handleStartDateChange = value => {
    setStartDate(value);
    if (value && endDate) {
      refreshLeaderboardData(value, endDate);
    }
  };

  const handleEndDateChange = value => {
    setEndDate(value);
    if (startDate && value) {
      refreshLeaderboardData(startDate, value);
    }
  };

  const getMedal = position => {
    switch (position) {
      case 1:
        return "🥇";
      case 2:
        return "🥈";
      case 3:
        return "🥉";
      default:
        return null;
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const formatTrainingTime = minutes => {
    if (!minutes || minutes === 0) return "0m";
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    if (hours > 0 && remainingMinutes > 0) {
      return `${hours}h ${remainingMinutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${remainingMinutes}m`;
    }
  };

  const calculateNextMilestone = () => {
    if (
      !trainingData ||
      !trainingData.your_rank ||
      trainingData.your_rank === 1
    ) {
      return null;
    }

    const yourRank = trainingData.your_rank;
    const nextRankIndex = yourRank - 2;

    if (nextRankIndex < 0 || nextRankIndex >= trainingData.leaderboard.length) {
      return null;
    }

    const currentUserEntry = trainingData.leaderboard.find(
      entry => entry.rep_id === user?.repId
    );
    const nextPerson = trainingData.leaderboard[nextRankIndex];

    if (!currentUserEntry || !nextPerson) return null;

    const minutesToPass = Math.ceil(
      nextPerson.minutes_watched - currentUserEntry.minutes_watched
    );

    return {
      name: `${nextPerson.first_name} ${nextPerson.last_name}`,
      minutesToPass: minutesToPass > 0 ? minutesToPass : 0,
    };
  };

  const getUnifiedLeaderboard = () => {
    if (isRecruitingMetric(selectedMetric)) {
      if (!recruitingData?.leaderboard) return [];

      let entries = [...recruitingData.leaderboard];

      switch (selectedMetric) {
        case "leads":
          entries.sort((a, b) => b.lead_count - a.lead_count);
          break;
        case "first_meetings":
          entries.sort((a, b) => b.first_meeting_count - a.first_meeting_count);
          break;
        case "additional_meetings":
          entries.sort(
            (a, b) => b.additional_meeting_count - a.additional_meeting_count
          );
          break;
        case "offers":
          entries.sort((a, b) => b.offer_sent_count - a.offer_sent_count);
          break;
        case "signed":
          entries.sort((a, b) => b.signed_count - a.signed_count);
          break;
        default:
          entries.sort((a, b) => b.total_score - a.total_score);
      }

      if (searchText) {
        entries = entries.filter(entry =>
          `${entry.first_name} ${entry.last_name}`
            .toLowerCase()
            .includes(searchText.toLowerCase())
        );
      }

      return entries.map(entry => ({
        rep_id: entry.rep_id,
        first_name: entry.first_name,
        last_name: entry.last_name,
        profile_image: entry.profile_image,
        value: getRecruitingMetricValue(entry, selectedMetric),
        total_score: entry.total_score,
        leads: entry.lead_count,
        first_meetings: entry.first_meeting_count,
        additional_meetings: entry.additional_meeting_count,
        offers: entry.offer_sent_count,
        signed: entry.signed_count,
        rank_change: entry.rank_change,
      }));
    } else {
      if (!trainingData?.leaderboard) return [];

      let entries = [...trainingData.leaderboard];

      if (searchText) {
        entries = entries.filter(entry =>
          `${entry.first_name} ${entry.last_name}`
            .toLowerCase()
            .includes(searchText.toLowerCase())
        );
      }

      if (selectedMetric === "total_minutes") {
        entries.sort((a, b) => b.minutes_watched - a.minutes_watched);
      } else {
        entries.sort((a, b) => b.videos_completed - a.videos_completed);
      }

      return entries.map(entry => ({
        rep_id: entry.rep_id,
        first_name: entry.first_name,
        last_name: entry.last_name,
        profile_image: entry.profile_image,
        value:
          selectedMetric === "total_minutes"
            ? formatTrainingTime(entry.minutes_watched)
            : entry.videos_completed,
        rank_change: entry.rank_change,
      }));
    }
  };

  const getRecruitingMetricValue = (entry, metric) => {
    switch (metric) {
      case "leads":
        return entry.lead_count;
      case "first_meetings":
        return entry.first_meeting_count;
      case "additional_meetings":
        return entry.additional_meeting_count;
      case "offers":
        return entry.offer_sent_count;
      case "signed":
        return entry.signed_count;
      default:
        return entry.total_score;
    }
  };

  const getMetricColumnHeader = () => {
    switch (selectedMetric) {
      case "total_minutes":
        return "Minutes";
      case "completed_videos":
        return "Videos";
      case "total_score":
        return "Score";
      case "leads":
        return "Leads";
      case "first_meetings":
        return "1st Mtgs";
      case "additional_meetings":
        return "Add'l Mtgs";
      case "offers":
        return "Offers";
      case "signed":
        return "Signed";
      default:
        return "Value";
    }
  };

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Paper p="xl" withBorder>
        <Center>
          <Stack align="center" spacing="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading leaderboard...</Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        <Text>{error}</Text>
        <Button size="sm" mt="md" onClick={loadAllData}>
          Retry
        </Button>
      </Alert>
    );
  }

  const unifiedLeaderboard = getUnifiedLeaderboard();
  const showRecruitingBreakdown = selectedMetric === "total_score";
  const nextMilestone = calculateNextMilestone();
  const currentData = isRecruitingMetric(selectedMetric)
    ? recruitingData
    : trainingData;
  const selectedMetricLabel =
    availableMetrics.find(m => m.value === selectedMetric)?.label || "Metric";
  const orgLevelFilters =
    trainingData?.org_type_filters || recruitingData?.org_type_filters || [];
  const hasOrgLevelFilters = orgLevelFilters.length > 0;

  return (
    <>
      {/* Search Bar */}
      <TextInput
        placeholder="Search participants..."
        leftSection={<IconSearch size={18} />}
        rightSection={
          searchText && (
            <IconX
              size={18}
              style={{ cursor: "pointer" }}
              onClick={() => setSearchText("")}
            />
          )
        }
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
        className="modern-search-bar"
        size="lg"
      />

      {/* Stat Cards */}
      <div className="stat-cards-container">
        {/* Current Streak */}
        <div className="stat-card stat-card-orange">
          <Text className="stat-card-label">CURRENT STREAK</Text>
          <Group gap="xs" align="center" justify="flex-start">
            <IconFlame size={24} style={{ color: "#f97316" }} />
            <Text className="stat-card-value">
              {streakData?.streak_days || 0}
            </Text>
          </Group>
        </div>

        {/* Your Momentum */}
        <div className="stat-card stat-card-blue">
          <Text className="stat-card-label">YOUR MOMENTUM</Text>
          {currentData?.your_rank &&
            (() => {
              const currentUserEntry = currentData.leaderboard?.find(
                entry => entry.rep_id === user?.repId
              );
              const rankChange = currentUserEntry?.rank_change || 0;

              return rankChange !== 0 ? (
                <Group gap={4} align="center" justify="flex-start">
                  {rankChange > 0 ? (
                    <>
                      <IconArrowUp size={20} style={{ color: "#2563eb" }} />
                      <Text
                        className="stat-card-value"
                        style={{ color: "#2563eb" }}
                      >
                        {rankChange}
                      </Text>
                    </>
                  ) : (
                    <>
                      <IconArrowDown size={20} style={{ color: "#dc2626" }} />
                      <Text
                        className="stat-card-value"
                        style={{ color: "#dc2626" }}
                      >
                        {Math.abs(rankChange)}
                      </Text>
                    </>
                  )}
                </Group>
              ) : (
                <Text className="stat-card-value" style={{ fontSize: "16px" }}>
                  No change
                </Text>
              );
            })()}
        </div>

        {/* Next Milestone */}
        <div className="stat-card stat-card-purple">
          <Text className="stat-card-label">NEXT MILESTONE</Text>
          {nextMilestone ? (
            <>
              <Group gap={4} align="center" justify="flex-start">
                <Text style={{ fontSize: "20px" }}>🏆</Text>
                <Text className="stat-card-value" style={{ fontSize: "16px" }}>
                  You're #1!
                </Text>
              </Group>
            </>
          ) : trainingData?.your_rank === 1 ? (
            <Group gap={4} align="center" justify="flex-start">
              <Text style={{ fontSize: "20px" }}>🏆</Text>
              <Text className="stat-card-value" style={{ fontSize: "16px" }}>
                You're #1!
              </Text>
            </Group>
          ) : (
            <Text className="stat-card-value" style={{ fontSize: "16px" }}>
              Loading...
            </Text>
          )}
        </div>
      </div>

      {/* Toggle Buttons */}
      <Group gap="sm">
        <Button
          variant="subtle"
          leftSection={<IconFilter size={16} />}
          onClick={() => setShowMetricModal(true)}
          disabled={isRefreshingLeaderboard}
          className="toggle-button"
          style={{
            backgroundColor: "white",
            color: "#6b7280",
            border: "1px solid #e5e7eb",
            borderRadius: "999px",
            fontWeight: 500,
          }}
        >
          {selectedMetricLabel}
        </Button>
        <Button
          variant="default"
          leftSection={<IconTrophy size={16} />}
          onClick={() => setShowTimePeriodModal(true)}
          disabled={isRefreshingLeaderboard}
          className="toggle-button"
          style={{
            backgroundColor: "white",
            color: "#6b7280",
            border: "1px solid #e5e7eb",
            borderRadius: "999px",
            fontWeight: 500,
          }}
        >
          {getTimePeriodLabel()}
        </Button>

        {hasOrgLevelFilters && (
          <Button
            variant="default"
            leftSection={<IconUsers size={16} />}
            onClick={() => setShowOrgLevelModal(true)}
            disabled={isRefreshingLeaderboard}
            className="toggle-button"
            style={{
              backgroundColor: "white",
              color: "#6b7280",
              border: "1px solid #e5e7eb",
              borderRadius: "999px",
              fontWeight: 500,
            }}
          >
            {selectedOrgLevelFilter
              ? selectedOrgLevelFilter.filter_name
              : "Sales Group"}
          </Button>
        )}

        {dateRangePreset === "custom" && (
          <>
            <Button
              variant="default"
              className="toggle-button custom-date-pill"
              disabled={isRefreshingLeaderboard}
              onClick={() => {
                if (startDateInputRef.current?.showPicker) {
                  startDateInputRef.current.showPicker();
                } else {
                  startDateInputRef.current?.click();
                }
              }}
            >
              {startDate
                ? `Start: ${formatDatePillLabel(startDate)}`
                : "Start Date"}
            </Button>

            <Button
              variant="default"
              className="toggle-button custom-date-pill"
              disabled={isRefreshingLeaderboard}
              onClick={() => {
                if (endDateInputRef.current?.showPicker) {
                  endDateInputRef.current.showPicker();
                } else {
                  endDateInputRef.current?.click();
                }
              }}
            >
              {endDate ? `End: ${formatDatePillLabel(endDate)}` : "End Date"}
            </Button>

            <input
              ref={startDateInputRef}
              type="date"
              value={startDate}
              onChange={e => handleStartDateChange(e.target.value)}
              className="hidden-date-input"
            />
            <input
              ref={endDateInputRef}
              type="date"
              value={endDate}
              onChange={e => handleEndDateChange(e.target.value)}
              className="hidden-date-input"
            />
          </>
        )}

        {isRefreshingLeaderboard && (
          <Group gap={6} align="center">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              Updating...
            </Text>
          </Group>
        )}
      </Group>

      {/* Unified Leaderboard Table */}
      <Paper withBorder className="leaderboard-table">
        {/* Header */}
        <div className="leaderboard-header">
          <Text className="leaderboard-header-text" style={{ width: 60 }}>
            RANK
          </Text>
          <Text className="leaderboard-header-text" style={{ flex: 1 }}>
            REP
          </Text>
          {showRecruitingBreakdown ? (
            <>
              <Text
                className="leaderboard-header-text"
                style={{ width: 65, textAlign: "right" }}
              >
                LEADS
              </Text>
              <Text
                className="leaderboard-header-text"
                style={{ width: 65, textAlign: "right" }}
              >
                1ST
              </Text>
              <Text
                className="leaderboard-header-text"
                style={{ width: 65, textAlign: "right" }}
              >
                ADD'L
              </Text>
              <Text
                className="leaderboard-header-text"
                style={{ width: 65, textAlign: "right" }}
              >
                OFFERS
              </Text>
              <Text
                className="leaderboard-header-text"
                style={{ width: 65, textAlign: "right" }}
              >
                SIGNED
              </Text>
              <Text
                className="leaderboard-header-text"
                style={{ width: 80, textAlign: "right" }}
              >
                TOTAL
              </Text>
            </>
          ) : (
            <Text
              className="leaderboard-header-text"
              style={{ width: 120, textAlign: "right" }}
            >
              {getMetricColumnHeader().toUpperCase()}
            </Text>
          )}
          <Text
            className="leaderboard-header-text"
            style={{ width: 50, textAlign: "right" }}
          >
            +/-
          </Text>
        </div>

        {/* Rows */}
        <div className="leaderboard-rows">
          {unifiedLeaderboard.length > 0 ? (
            unifiedLeaderboard.map((rep, index) => {
              const rank = index + 1;
              const isCurrentUser = rep.rep_id === user?.repId;
              const fullName = `${rep.first_name} ${rep.last_name}`;

              return (
                <div
                  key={rep.rep_id}
                  className={`leaderboard-row ${isCurrentUser ? "current-user" : ""}`}
                >
                  {/* Rank */}
                  <div className="leaderboard-rank" style={{ width: 60 }}>
                    {getMedal(rank) ? (
                      <Text style={{ fontSize: "24px" }}>{getMedal(rank)}</Text>
                    ) : (
                      <Text className="rank-number">#{rank}</Text>
                    )}
                  </div>

                  {/* Rep Info */}
                  <Group style={{ flex: 1 }} gap="sm">
                    <Avatar
                      src={rep.profile_image}
                      alt={fullName}
                      size={40}
                      radius="xl"
                      color="orange"
                    >
                      {getInitials(rep.first_name, rep.last_name)}
                    </Avatar>
                    <Text className="rep-name">{fullName}</Text>
                  </Group>

                  {/* Value / Breakdown */}
                  {showRecruitingBreakdown ? (
                    <>
                      <Text
                        className="rep-value"
                        style={{ width: 65, textAlign: "right" }}
                      >
                        {rep.leads}
                      </Text>
                      <Text
                        className="rep-value"
                        style={{ width: 65, textAlign: "right" }}
                      >
                        {rep.first_meetings}
                      </Text>
                      <Text
                        className="rep-value"
                        style={{ width: 65, textAlign: "right" }}
                      >
                        {rep.additional_meetings}
                      </Text>
                      <Text
                        className="rep-value"
                        style={{ width: 65, textAlign: "right" }}
                      >
                        {rep.offers}
                      </Text>
                      <Text
                        className="rep-value"
                        style={{ width: 65, textAlign: "right" }}
                      >
                        {rep.signed}
                      </Text>
                      <Text
                        className="rep-value"
                        style={{
                          width: 80,
                          textAlign: "right",
                          fontWeight: 700,
                        }}
                      >
                        {rep.total_score}
                      </Text>
                    </>
                  ) : (
                    <Text
                      className="rep-value"
                      style={{ width: 120, textAlign: "right" }}
                    >
                      {rep.value}
                    </Text>
                  )}

                  {/* Rank Change */}
                  <div
                    className="rank-change"
                    style={{ width: 50, textAlign: "right" }}
                  >
                    {rep.rank_change && rep.rank_change !== 0 ? (
                      <span
                        className={
                          rep.rank_change > 0 ? "rank-up" : "rank-down"
                        }
                      >
                        {rep.rank_change > 0 ? "+" : ""}
                        {rep.rank_change}
                      </span>
                    ) : (
                      <Text size="xs" c="dimmed">
                        —
                      </Text>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <Center p="xl">
              <Text c="dimmed">
                {searchText ? "No participants found" : "No data available"}
              </Text>
            </Center>
          )}
        </div>
      </Paper>

      {/* Metric Selection Modal */}
      <Modal
        opened={showMetricModal}
        onClose={() => setShowMetricModal(false)}
        title="Select Metric"
        size="sm"
        classNames={{
          content: "performance-modal-content",
          header: "performance-modal-header",
          title: "performance-modal-title",
          body: "performance-modal-body",
        }}
      >
        <Stack spacing="xs">
          {availableMetrics.reduce((acc, metric, index, array) => {
            if (index === 0 || metric.section !== array[index - 1].section) {
              acc.push(
                <Box
                  key={`header-${metric.section}`}
                  className="metric-section-header"
                  mt={index > 0 ? "md" : 0}
                >
                  <Text
                    size="xs"
                    fw={700}
                    className="metric-section-header-text"
                  >
                    {metric.section}
                  </Text>
                </Box>
              );
            }

            acc.push(
              <Button
                key={metric.value}
                variant={selectedMetric === metric.value ? "filled" : "subtle"}
                disabled={isRefreshingLeaderboard}
                onClick={() => {
                  setSelectedMetric(metric.value);
                  setShowMetricModal(false);
                  if (isRecruitingMetric(metric.value) && !recruitingData) {
                    fetchRecruitingLeaderboard(
                      startDate,
                      endDate,
                      selectedOrgLevelFilter
                    );
                  }
                }}
                fullWidth
                justify="flex-start"
              >
                {metric.label}
              </Button>
            );

            return acc;
          }, [])}
        </Stack>
      </Modal>

      {/* Time Period Selection Modal */}
      <Modal
        opened={showTimePeriodModal}
        onClose={() => setShowTimePeriodModal(false)}
        title="Select Time Period"
        size="sm"
        className="time-filter-modal"
        classNames={{
          content: "performance-modal-content",
          header: "performance-modal-header",
          title: "performance-modal-title",
          body: "performance-modal-body",
        }}
      >
        <Stack spacing="xs">
          {datePresets.map(preset => (
            <Button
              key={preset.value}
              variant="subtle"
              disabled={isRefreshingLeaderboard}
              style={
                dateRangePreset === preset.value
                  ? {
                      backgroundColor: "#eef2ff",
                      border: "1px solid #c7d2fe",
                      color: "#334155",
                    }
                  : {
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e7edf5",
                      color: "#334155",
                    }
              }
              onClick={() => {
                if (preset.value === "custom") {
                  setDateRangePreset("custom");
                  setShowTimePeriodModal(false);
                } else {
                  applyDateRangePreset(preset.value);
                  setShowTimePeriodModal(false);
                }
              }}
              fullWidth
              justify="flex-start"
            >
              {preset.label}
            </Button>
          ))}

          {dateRangePreset === "custom" && (
            <Box mt="md">
              <Stack spacing="sm">
                <TextInput
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
                <TextInput
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                />
                <Button
                  variant="subtle"
                  style={{
                    backgroundColor: "#eef2ff",
                    border: "1px solid #c7d2fe",
                    color: "#334155",
                  }}
                  onClick={async () => {
                    await refreshLeaderboardData(startDate, endDate);
                    setShowTimePeriodModal(false);
                  }}
                  disabled={!startDate || !endDate || isRefreshingLeaderboard}
                >
                  Apply Custom Range
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Modal>

      {/* Sales Group Filter Modal */}
      <Modal
        opened={showOrgLevelModal}
        onClose={() => setShowOrgLevelModal(false)}
        title="Select Sales Group"
        size="sm"
        classNames={{
          content: "performance-modal-content",
          header: "performance-modal-header",
          title: "performance-modal-title",
          body: "performance-modal-body",
        }}
      >
        <Stack spacing="xs">
          <Button
            variant="subtle"
            disabled={isRefreshingLeaderboard}
            style={
              !selectedOrgLevelFilter
                ? {
                    backgroundColor: "#eef2ff",
                    border: "1px solid #c7d2fe",
                    color: "#334155",
                  }
                : {
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e7edf5",
                    color: "#334155",
                  }
            }
            onClick={() => handleOrgLevelFilterChange(null)}
            fullWidth
            justify="flex-start"
          >
            All
          </Button>
          {orgLevelFilters.map(filter => (
            <Button
              key={filter.filter_value}
              variant="subtle"
              disabled={isRefreshingLeaderboard}
              style={
                selectedOrgLevelFilter?.filter_value === filter.filter_value
                  ? {
                      backgroundColor: "#eef2ff",
                      border: "1px solid #c7d2fe",
                      color: "#334155",
                    }
                  : {
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e7edf5",
                      color: "#334155",
                    }
              }
              onClick={() => handleOrgLevelFilterChange(filter)}
              fullWidth
              justify="flex-start"
            >
              {filter.filter_name}
            </Button>
          ))}
        </Stack>
      </Modal>
    </>
  );
}

export default LeaderboardTab;
